import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/AccountsAPI/verifyBankAccountsAPI';

const err = (e, fallback) => e?.ErrorDetails?.ErrorMessage || e?.Message || e?.message || fallback;

export const fetchVerifyBankAccountsQueue = createAsyncThunk(
    'verifyBankAccounts/fetchQueue',
    async (roleId, { rejectWithValue }) => {
        try { return await api.getVerificationBankAccounts(roleId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank account verification queue')); }
    }
);

export const fetchVerifyBankAccountDetail = createAsyncThunk(
    'verifyBankAccounts/fetchDetail',
    async (bankId, { rejectWithValue }) => {
        try { return await api.getVerificationBankById(bankId); }
        catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank account details')); }
    }
);

export const approveVerifyBankAccount = createAsyncThunk(
    'verifyBankAccounts/approve',
    async (payload, { rejectWithValue }) => {
        try { return await api.approveBankAccounts(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to submit bank account verification')); }
    }
);

export const updateReturnedBankAccount = createAsyncThunk(
    'verifyBankAccounts/updateReturned',
    async (payload, { rejectWithValue }) => {
        try { return await api.updateReturnedBankAccount(payload); }
        catch (e) { return rejectWithValue(err(e, 'Failed to update returned bank account')); }
    }
);

const initialState = {
    queue: [], queueLoading: false, queueError: null,
    detail: null, detailLoading: false, detailError: null,
    verifyLoading: false, verifyError: null,
    updateLoading: false, updateError: null,
};

const verifyBankAccountsSlice = createSlice({
    name: 'verifyBankAccounts',
    initialState,
    reducers: {
        clearVerifyBankAccountDetail: (s) => { s.detail = null; s.detailError = null; s.detailLoading = false; },
        resetVerifyBankAccounts: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVerifyBankAccountsQueue.pending, (s) => { s.queueLoading = true; s.queueError = null; })
            .addCase(fetchVerifyBankAccountsQueue.fulfilled, (s, a) => { s.queueLoading = false; s.queue = a.payload?.Data || []; })
            .addCase(fetchVerifyBankAccountsQueue.rejected, (s, a) => { s.queueLoading = false; s.queueError = a.payload; s.queue = []; });

        builder
            .addCase(fetchVerifyBankAccountDetail.pending, (s) => { s.detailLoading = true; s.detailError = null; s.detail = null; })
            .addCase(fetchVerifyBankAccountDetail.fulfilled, (s, a) => { s.detailLoading = false; s.detail = a.payload?.Data || null; })
            .addCase(fetchVerifyBankAccountDetail.rejected, (s, a) => { s.detailLoading = false; s.detailError = a.payload; s.detail = null; });

        builder
            .addCase(approveVerifyBankAccount.pending, (s) => { s.verifyLoading = true; s.verifyError = null; })
            .addCase(approveVerifyBankAccount.fulfilled, (s) => { s.verifyLoading = false; })
            .addCase(approveVerifyBankAccount.rejected, (s, a) => { s.verifyLoading = false; s.verifyError = a.payload; });

        builder
            .addCase(updateReturnedBankAccount.pending, (s) => { s.updateLoading = true; s.updateError = null; })
            .addCase(updateReturnedBankAccount.fulfilled, (s) => { s.updateLoading = false; })
            .addCase(updateReturnedBankAccount.rejected, (s, a) => { s.updateLoading = false; s.updateError = a.payload; });
    },
});

export const { clearVerifyBankAccountDetail, resetVerifyBankAccounts } = verifyBankAccountsSlice.actions;

export const selectVBAQueue = (s) => s.verifyBankAccounts.queue;
export const selectVBAQueueLoading = (s) => s.verifyBankAccounts.queueLoading;
export const selectVBAQueueError = (s) => s.verifyBankAccounts.queueError;
export const selectVBADetail = (s) => s.verifyBankAccounts.detail;
export const selectVBADetailLoading = (s) => s.verifyBankAccounts.detailLoading;
export const selectVBADetailError = (s) => s.verifyBankAccounts.detailError;
export const selectVBAVerifyLoading = (s) => s.verifyBankAccounts.verifyLoading;
export const selectVBAUpdateLoading = (s) => s.verifyBankAccounts.updateLoading;

export default verifyBankAccountsSlice.reducer;
